import { useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Building2, Check, Stethoscope, User } from "lucide-react";

import { Button } from "@/components/ui/button";
import { LoadErrorState } from "@/components/ui/load-error-state";

/**
 * Usage tab on the unified billing page — 1:1 port of legacy
 * `UsageSection` (multi-scope mode) + `usage-dashboard-client.tsx`.
 * Data: GET /api/account/usage (all scopes the user can see).
 *
 * Adaptation vs legacy: the upgrade CTA navigates to /billing/subscribe
 * (the SPA plan-selection flow) instead of the legacy FeatureUpgradeProvider
 * modal, which has no apps/app equivalent yet.
 */

type UsageScopeKey = "user" | "provider" | "facility" | "business";
type UsagePeriodKey = "month" | "year" | "lifetime";
type FeatureAccess = "included" | "limited" | "soon" | "hidden";

interface PlanLimitCell {
  access: FeatureAccess;
  limit: number | null;
}

interface QuotaUsageItem {
  key: string;
  labelRo: string;
  labelEn: string;
  category: string;
  access: FeatureAccess;
  limit: number;
  usage: number;
  exhausted: boolean;
  upgradeTo: string | null;
  periodKey: UsagePeriodKey;
  limitsByPlan: Record<string, PlanLimitCell>;
}

interface PlanColumn {
  code: string;
  name: Record<string, string>;
  isCurrent: boolean;
}

interface UsageScopeData {
  scopeKey: UsageScopeKey;
  ownerId: string;
  label?: string;
  planCode: string;
  plans: PlanColumn[];
  features: QuotaUsageItem[];
}

interface UsageDashboardData {
  scopes: UsageScopeData[];
}

export function UsageDashboard() {
  const query = useQuery({
    queryKey: ["api", "/api/account/usage"],
    queryFn: () => apiFetch.get<UsageDashboardData>("/api/account/usage"),
  });
  const [activeScopeIdx, setActiveScopeIdx] = useState(0);

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        <div className="bg-muted h-10 w-64 animate-pulse rounded-lg" />
        <div className="bg-muted h-64 animate-pulse rounded-xl" />
      </div>
    );
  }

  // P16: an API failure is not "no limits" — a free user would read a false promise.
  // A failed background refetch keeps the data already shown.
  if (query.isError && !query.data) {
    return (
      <LoadErrorState
        title={<Trans>Could not load your usage.</Trans>}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }

  const scopes = query.data?.scopes ?? [];
  const activeScope = scopes[Math.min(activeScopeIdx, Math.max(0, scopes.length - 1))];

  if (!activeScope) {
    return (
      <div className="bg-card border-border rounded-xl border p-8 text-center">
        <p className="text-muted-foreground text-sm">
          <Trans>No limits on your current plan.</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      {scopes.length > 1 && (
        <div
          role="tablist"
          className="bg-muted text-muted-foreground inline-flex max-w-full flex-wrap gap-1 rounded-lg p-1"
        >
          {scopes.map((scope, idx) => {
            const isActive = idx === activeScopeIdx;
            return (
              <button
                key={`${scope.scopeKey}-${scope.ownerId}`}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => setActiveScopeIdx(idx)}
                className={cn(
                  "inline-flex min-h-[44px] items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "bg-background text-foreground shadow-sm" : "hover:text-foreground",
                )}
              >
                <ScopeIcon scopeKey={scope.scopeKey} />
                <span className="truncate">
                  <ScopeLabel scope={scope} />
                </span>
              </button>
            );
          })}
        </div>
      )}

      <ScopePanel scope={activeScope} />
    </div>
  );
}

function ScopeIcon({ scopeKey }: { scopeKey: UsageScopeKey }) {
  const className = "size-4 shrink-0";
  if (scopeKey === "provider") return <Stethoscope className={className} />;
  if (scopeKey === "facility") return <Building2 className={className} />;
  return <User className={className} />;
}

function ScopeLabel({ scope }: { scope: UsageScopeData }) {
  if (scope.scopeKey === "provider") return <Trans>Doctor</Trans>;
  if (scope.scopeKey === "facility")
    return scope.label ? <>{scope.label}</> : <Trans>Clinic</Trans>;
  return <Trans>Personal</Trans>;
}

function planLabel(plan: PlanColumn, locale: string): string {
  return plan.name[locale] ?? plan.name.en ?? plan.name.ro ?? plan.code;
}

function ScopePanel({ scope }: { scope: UsageScopeData }) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const navigate = useNavigate();

  const grouped = useMemo(() => {
    const map = new Map<string, QuotaUsageItem[]>();
    for (const item of scope.features) {
      const arr = map.get(item.category) ?? [];
      arr.push(item);
      map.set(item.category, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [scope.features]);

  if (scope.features.length === 0) {
    return (
      <div className="bg-card border-border rounded-xl border p-8 text-center">
        <p className="text-muted-foreground text-sm">
          <Trans>No limits on your current plan.</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.map(([category, items]) => (
        <section key={category} className="bg-card border-border overflow-hidden rounded-xl border">
          <h2 className="border-border text-foreground border-b px-4 py-3 text-sm font-semibold tracking-wide uppercase sm:px-6">
            {category.replace(/_/g, " ")}
          </h2>

          {/* Desktop / tablet: comparison table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-border bg-muted/30 border-b">
                  <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium uppercase sm:px-6">
                    <Trans>Feature</Trans>
                  </th>
                  {scope.plans.map((plan) => (
                    <th
                      key={plan.code}
                      className={cn(
                        "px-4 py-3 text-center text-xs font-medium uppercase",
                        plan.isCurrent ? "text-primary bg-primary/5" : "text-muted-foreground",
                      )}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span>{planLabel(plan, locale)}</span>
                        {plan.isCurrent && (
                          <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-[0.625rem] font-semibold normal-case">
                            <Trans>Your plan</Trans>
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {items.map((item) => (
                  <QuotaTableRow key={item.key} item={item} plans={scope.plans} locale={locale} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked card per feature */}
          <div className="divide-border divide-y md:hidden">
            {items.map((item) => (
              <QuotaCard key={item.key} item={item} plans={scope.plans} locale={locale} />
            ))}
          </div>
        </section>
      ))}

      <div className="flex justify-center pt-2">
        <Button
          variant="outline"
          className="min-h-[44px] gap-2"
          onClick={() =>
            navigate({
              to: "/billing/subscribe",
              search: { interval: "monthly", target: scope.scopeKey },
            })
          }
        >
          <Trans>View all plans</Trans>
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function FormattedLimit({ cell }: { cell: PlanLimitCell }) {
  if (cell.access === "included") return <Trans>Included</Trans>;
  if (cell.access === "soon") return <Trans>Coming soon</Trans>;
  if (cell.access === "limited") {
    if (cell.limit === -1) return <>∞</>;
    if (cell.limit == null || cell.limit === 0) return <>—</>;
    return <>{cell.limit}</>;
  }
  return <>—</>;
}

function UpgradeButton({ planCode }: { planCode: string }) {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={() =>
        navigate({
          to: "/billing/subscribe",
          search: { plan: planCode, interval: "monthly" },
        })
      }
      className="min-h-[44px] px-3 text-xs"
    >
      <Trans>Upgrade</Trans>
    </Button>
  );
}

function QuotaTableRow({
  item,
  plans,
  locale,
}: {
  item: QuotaUsageItem;
  plans: PlanColumn[];
  locale: string;
}) {
  const label = locale === "ro" ? item.labelRo : item.labelEn;
  const pct = item.limit > 0 ? Math.min(100, (item.usage / item.limit) * 100) : 0;
  const color = colorForPct(pct, item.exhausted);

  return (
    <tr>
      <td className="px-4 py-3 align-top sm:px-6">
        <div className="text-foreground text-sm font-medium">{label}</div>
        <div className="text-muted-foreground mt-0.5 text-xs">
          <PeriodLabel periodKey={item.periodKey} />
        </div>
      </td>
      {plans.map((plan) => {
        const cell = item.limitsByPlan[plan.code];
        const isUnlimited = cell?.access === "limited" && cell.limit === -1;
        const showUsage =
          plan.isCurrent &&
          cell?.access === "limited" &&
          cell.limit !== -1 &&
          (cell.limit ?? 0) > 0;
        return (
          <td
            key={plan.code}
            className={cn("px-4 py-3 text-center align-top", plan.isCurrent && "bg-primary/5")}
          >
            {showUsage ? (
              <div className="flex flex-col items-center gap-1.5">
                <span
                  className={cn(
                    "text-sm font-semibold tabular-nums",
                    item.exhausted ? "text-destructive" : "text-foreground",
                  )}
                >
                  <Trans>
                    {item.usage} of {cell?.limit ?? 0}
                  </Trans>
                </span>
                <div className="bg-muted relative h-1.5 w-full max-w-[120px] overflow-hidden rounded-full">
                  <div
                    className={cn("h-full rounded-full transition-all duration-300", color)}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {item.exhausted && item.upgradeTo && <UpgradeButton planCode={item.upgradeTo} />}
              </div>
            ) : isUnlimited ? (
              <span className="text-success inline-flex items-center gap-1 text-sm font-semibold">
                <Check className="size-4" />
                <Trans>Unlimited</Trans>
              </span>
            ) : (
              <span className="text-foreground text-sm font-medium tabular-nums">
                <FormattedLimit cell={cell ?? { access: "hidden", limit: null }} />
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function QuotaCard({
  item,
  plans,
  locale,
}: {
  item: QuotaUsageItem;
  plans: PlanColumn[];
  locale: string;
}) {
  const label = locale === "ro" ? item.labelRo : item.labelEn;
  const pct = item.limit > 0 ? Math.min(100, (item.usage / item.limit) * 100) : 0;
  const color = colorForPct(pct, item.exhausted);
  const currentPlan = plans.find((p) => p.isCurrent);

  const showUsage = currentPlan && item.access === "limited" && item.limit !== -1 && item.limit > 0;

  return (
    <div className="space-y-3 p-4">
      <div>
        <p className="text-foreground text-sm font-medium">{label}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">
          <PeriodLabel periodKey={item.periodKey} />
        </p>
      </div>

      {showUsage && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                "text-sm font-semibold tabular-nums",
                item.exhausted ? "text-destructive" : "text-foreground",
              )}
            >
              <Trans>
                {item.usage} of {item.limit}
              </Trans>
            </span>
            {item.exhausted && item.upgradeTo && <UpgradeButton planCode={item.upgradeTo} />}
          </div>
          <div className="bg-muted relative h-1.5 w-full overflow-hidden rounded-full">
            <div
              className={cn("h-full rounded-full transition-all duration-300", color)}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      <div className="divide-border -mx-1 divide-y rounded-md">
        {plans.map((plan) => {
          const cell = item.limitsByPlan[plan.code];
          return (
            <div
              key={plan.code}
              className={cn(
                "flex items-center justify-between px-2 py-2 text-xs",
                plan.isCurrent && "bg-primary/5 rounded-md",
              )}
            >
              <span
                className={cn(
                  "font-medium",
                  plan.isCurrent ? "text-primary" : "text-muted-foreground",
                )}
              >
                {planLabel(plan, locale)}
                {plan.isCurrent && (
                  <span className="bg-primary text-primary-foreground ml-2 rounded-full px-1.5 py-0.5 text-[0.625rem] font-semibold">
                    <Trans>Your plan</Trans>
                  </span>
                )}
              </span>
              <span
                className={cn(
                  "font-semibold tabular-nums",
                  cell?.access === "limited" && cell.limit === -1
                    ? "text-success"
                    : "text-foreground",
                )}
              >
                <FormattedLimit cell={cell ?? { access: "hidden", limit: null }} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function colorForPct(pct: number, exhausted: boolean): string {
  if (exhausted || pct >= 100) return "bg-destructive";
  if (pct >= 80) return "bg-warning";
  return "bg-success";
}

function PeriodLabel({ periodKey }: { periodKey: UsagePeriodKey }) {
  if (periodKey === "month") return <Trans>Current month</Trans>;
  if (periodKey === "year") return <Trans>Current year</Trans>;
  return <Trans>Lifetime</Trans>;
}
